"use client";

import { useCallback, useSyncExternalStore } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import {
  clearFormRegistry,
  getFormEntries,
  subscribeFormInspector,
  type FormDebugEntry,
} from "../stores/form-inspector-store";

function StatusBadge({
  active,
  label,
  color,
}: {
  active: boolean;
  label: string;
  color: string;
}) {
  return (
    <span
      className={cn(
        "px-1.5 py-0.5 rounded text-sm font-mono",
        active ? color : "bg-muted/40 text-muted-foreground/40",
      )}
    >
      {label}
    </span>
  );
}

function FormRow({ entry }: { entry: FormDebugEntry }) {
  const t = useTranslations("debug.forms");
  const errorKeys = Object.keys(entry.errors);

  return (
    <div className="py-2 px-2 rounded hover:bg-muted/20 text-sm border-b border-border/10 last:border-0">
      {/* Header row */}
      <div className="flex items-center gap-2 mb-1.5">
        <span className="font-semibold text-foreground/90">{entry.name}</span>
        <div className="flex items-center gap-1 flex-wrap">
          <StatusBadge
            active={entry.isDirty}
            label={t("statuses.dirty")}
            color="bg-amber-500/10 text-amber-500"
          />
          <StatusBadge
            active={!entry.isValid}
            label={t("statuses.invalid")}
            color="bg-red-500/10 text-red-500"
          />
          <StatusBadge
            active={entry.isSubmitting}
            label={t("statuses.submitting")}
            color="bg-blue-500/10 text-blue-500"
          />
          <StatusBadge
            active={entry.isSubmitted}
            label={t("statuses.submitted")}
            color="bg-emerald-500/10 text-emerald-500"
          />
        </div>
        {entry.submitCount > 0 && (
          <span className="ml-auto text-sm text-muted-foreground/60 font-mono">
            {t("submitCount", { count: entry.submitCount })}
          </span>
        )}
      </div>

      {/* Errors */}
      {errorKeys.length > 0 && (
        <div className="mt-1 space-y-0.5 pl-2 border-l-2 border-red-500/30">
          {errorKeys.map((key) => (
            <div key={key} className="flex items-center gap-2">
              <span className="text-red-400 font-mono">{key}</span>
              {entry.errors[key]?.message && (
                <span className="text-muted-foreground/70">
                  {entry.errors[key].message}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FormInspectorPanel() {
  const t = useTranslations("debug.forms");
  const forms = useSyncExternalStore(
    subscribeFormInspector,
    getFormEntries,
    getFormEntries,
  );

  const handleClear = useCallback(() => {
    clearFormRegistry();
  }, []);

  if (forms.length === 0) {
    return (
      <div className="space-y-3">
        <div className="text-sm text-muted-foreground py-4 text-center">
          {t("emptyBefore")}{" "}
          <code className="font-mono text-sm bg-muted px-1 rounded">
            {"useForm"}
          </code>{" "}
          {t("emptyBetween")}{" "}
          <code className="font-mono text-sm bg-muted px-1 rounded">
            {"useDebugForm"}
          </code>{" "}
          {t("emptyAfter")}
        </div>
        <div className="text-sm text-muted-foreground/60 text-center font-mono">
          {t("importKeyword")} {"{ useDebugForm }"} {t("fromKeyword")}{" "}
          {'"@/components/debug/hooks/use-debug-form"'}
        </div>
      </div>
    );
  }

  const dirtyCount = forms.filter((f) => f.isDirty).length;
  const invalidCount = forms.filter((f) => !f.isValid).length;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>{t("trackedCount", { count: forms.length })}</span>
          {dirtyCount > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500">
              {t("dirtyCount", { count: dirtyCount })}
            </span>
          )}
          {invalidCount > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-500">
              {t("invalidCount", { count: invalidCount })}
            </span>
          )}
        </div>
        <button
          onClick={handleClear}
          className="text-sm px-2 py-0.5 rounded border border-border bg-muted/50 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          {t("clear")}
        </button>
      </div>
      <div>
        {forms.map((entry) => (
          <FormRow key={entry.id} entry={entry} />
        ))}
      </div>
    </div>
  );
}
